import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import useAuthStore from '@/stores/useAuthStore'
import { useToast } from '@/components/ui/use-toast'

export const LoginButtons = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isDevLoading, setIsDevLoading] = useState(false)
  const [isTestLoading, setIsTestLoading] = useState(false)
  const [isAnonymousLoading, setIsAnonymousLoading] = useState(false)
  const navigate = useNavigate()
  const { loginAsDevelopmentUser, loginAsTestUser, loginAsAnonymousUser } =
    useAuthStore()
  const { toast } = useToast()

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true)
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

    if (!googleClientId) {
      console.error('VITE_GOOGLE_CLIENT_ID is not defined in .env file')
      toast({
        variant: 'destructive',
        title: 'Configuration Error',
        description: 'Google Client ID is not configured.',
      })
      setIsGoogleLoading(false)
      return
    }

    const redirectUri = `${window.location.origin}/auth/google`
    const nonce =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')
    authUrl.searchParams.set('client_id', googleClientId)
    authUrl.searchParams.set('redirect_uri', redirectUri)
    authUrl.searchParams.set('response_type', 'id_token')
    authUrl.searchParams.set('scope', 'openid email profile')
    authUrl.searchParams.set('nonce', nonce)

    window.location.href = authUrl.toString()
  }

  const handleDevLogin = async () => {
    setIsDevLoading(true)
    try {
      await loginAsDevelopmentUser()
      toast({
        title: 'Development Login Successful',
        description: 'Welcome, Dev User!',
      })
      navigate('/history', { replace: true })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Development Login Failed',
        description:
          error instanceof Error ? error.message : 'An unknown error occurred.',
      })
    } finally {
      setIsDevLoading(false)
    }
  }

  const handleTestLogin = async () => {
    setIsTestLoading(true)
    try {
      await loginAsTestUser()
      toast({
        title: 'Test Login Successful',
        description: 'Welcome, Test User!',
      })
      navigate('/history', { replace: true })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Test Login Failed',
        description:
          error instanceof Error ? error.message : 'An unknown error occurred.',
      })
    } finally {
      setIsTestLoading(false)
    }
  }

  const handleAnonymousLogin = async () => {
    setIsAnonymousLoading(true)
    try {
      await loginAsAnonymousUser()
      toast({
        title: 'Anonymous Login Successful',
        description: 'Welcome, Anonymous User!',
      })
      navigate('/history', { replace: true })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Anonymous Login Failed',
        description:
          error instanceof Error ? error.message : 'An unknown error occurred.',
      })
    } finally {
      setIsAnonymousLoading(false)
    }
  }

  const isLoading =
    isGoogleLoading || isDevLoading || isTestLoading || isAnonymousLoading

  return (
    <div className="flex flex-col gap-4">
      <Button
        onClick={handleGoogleLogin}
        disabled={isLoading}
        className="w-full"
      >
        {isGoogleLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <img
            src="https://img.usecurling.com/i?q=google&color=white"
            alt="Google"
            className="mr-2 h-4 w-4"
          />
        )}
        {isGoogleLoading ? 'Redirecting...' : 'Sign in with Google'}
      </Button>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            For Dev & Testing
          </span>
        </div>
      </div>
      <Button
        onClick={handleAnonymousLogin}
        disabled={isLoading}
        variant="secondary"
        className="w-full"
      >
        {isAnonymousLoading && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        Use Anonymous User
      </Button>
      <Button
        onClick={handleTestLogin}
        disabled={isLoading}
        variant="secondary"
        className="w-full"
      >
        {isTestLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Use Test User
      </Button>
      <Button
        onClick={handleDevLogin}
        disabled={isLoading}
        variant="secondary"
        className="w-full"
      >
        {isDevLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Use Development User
      </Button>
    </div>
  )
}
